import type { DateString } from '~/types/CommonTypes';
import type { MySsApiResponseData, MySsApiResponseDataList } from '~/types/MySsApiResponse';
import type { MySsException } from '~/types/MySsException';

import usePaginator from '~/composables/usePaginator';
import { Transaction, TransactionListFilters } from '~/models/Transaction';
import { TransactionAdapter, TransactionListFilterAdapter } from '~adapters/TransactionAdapter';
import { defineStore } from 'pinia';
import { getBalance, getTransactions, makeARansomRequest } from '~services/apiMyAccount';
import { useAuthStore } from '~/stores/useAuthStore';
import { useTimer } from '~/composables/useTimer';

type TBalance = {
  availableBalance: number;
  lastUpdateDateTime: DateString;
  currencySymbol: string;
};

export const useTransactionsStore = defineStore(
  'MY_SCAN_SOURCE_TRANSACTIONS_STORE',
  () => {
    // #region States:
    const AUTH_STORE = useAuthStore();
    const LAYOUT_STORE = useLayoutStore();
    const PROFILE_STORE = useProfileStore();

    const { profile } = storeToRefs(PROFILE_STORE);

    const paginator = usePaginator();
    const timer = useTimer();
    const {
      pagination,
      CallPaginate,
      CallResetPagination,
      CallSetPagination,
      CallPaginateAndFetch,
    } = paginator;

    const balance = ref<TBalance>({} as TBalance);
    const transactionList = ref<Transaction[]>([]);
    const transactionListFilters = ref(new TransactionListFilters());
    const loadingTransactionList = ref<boolean>(false);
    const loadingBalance = ref<boolean>(false);
    // #endregion

    // #region Getters:
    const getTransactionListState = computed(() => transactionList.value);
    const getBalanceState = computed(() => balance.value);
    const hasTransaction = computed(() => transactionList.value.length > 0);
    // #endregion

    // #region Actions:
    function toggleIsLoadingBalance() {
      loadingBalance.value = !loadingBalance.value;
    }
    function toggleIsLoadingTransactionList() {
      loadingTransactionList.value = !loadingTransactionList.value;
    }
    function setBalance(data?: TBalance) {
      if (!data) balance.value = {} as TBalance;
      else balance.value = { ...data };
    }
    function setTransactionList(data?: Transaction[]) {
      if (!data) transactionList.value = [];
      else transactionList.value = [...data];
    }
    function $reset() {
      setTransactionList();
      setBalance();
      transactionListFilters.value = new TransactionListFilters();
      CallResetPagination();
    }
    // #endregion
    // #region Dispatchers:
    async function dispatchMakeARansomRequest(data: any) {
      const formData = new FormData();
      formData.append('CompanyDocument', data.CompanyDocument);
      formData.append('File', data.File);
      formData.append('Value', data.Value);
      formData.append('Description', data.Description);
      formData.append('OperationType', data.OperationType);

      return await makeARansomRequest(formData).then(
        (res) => res,
        (error: MySsException) => error,
      );
    }
    async function dispatchGetBalance(force = false) {
      if(profile.value.isCommercial) return
      if (!timer.isExpired.value && !force) return;

      toggleIsLoadingBalance();

      await getBalance(AUTH_STORE.session.currentEconomicGroupMember).then(
        ({ data }: MySsApiResponseData) => {
          setBalance(data);
          timer.startTimer();
          toggleIsLoadingBalance();
        },
        (error: MySsException) => {
          setBalance();
          toggleIsLoadingBalance();
          throw error;
        },
      );
    }

    async function dispatchGetTransactions(
      force = false,
      params = new TransactionListFilters(),
    ) {
      if(profile.value.isCommercial) return
      if (!timer.isExpired.value && !force) return;

      toggleIsLoadingTransactionList();

      const _params = TransactionListFilterAdapter.toDto({
        ...params,
        ...transactionListFilters.value,
        ...pagination.value,
      });

      await getTransactions(AUTH_STORE.session.currentEconomicGroupMember, _params).then(
        ({ data }: MySsApiResponseDataList) => {
          const { items, pagination } = data;
          setTransactionList(items.map(TransactionAdapter.toView));
          CallSetPagination(pagination);
          timer.startTimer();
          toggleIsLoadingTransactionList();
        },
        (error: MySsException) => {
          setTransactionList();
          CallResetPagination();
          toggleIsLoadingTransactionList();
          throw error;
        },
      );
    }
    // #endregion

    // #region Events:
    async function onSessionOpen() {

      return Promise.all([dispatchGetBalance(), dispatchGetTransactions()]).then(
        (result) => {
          const [balance, transactions] = result;
          console.log('get_balance ->>', balance);
          console.log('get_transactions ->>', transactions);
        },
      );
    }
    async function onChangeEconomicGroup() {
      transactionListFilters.value = new TransactionListFilters();
      return Promise.all([dispatchGetBalance(true), dispatchGetTransactions(true)]).then(
        () => {},
      );
    }
    // #endregion

    return {
      balance: getBalanceState,
      transactionList: getTransactionListState,
      hasTransaction,
      transactionPagination: pagination,
      transactionListFilters,
      CallPaginateAndFetch,
      CallPaginate,
      dispatchMakeARansomRequest,
      dispatchGetBalance,
      dispatchGetTransactions,
      onSessionOpen,
      onSessionClose: $reset,
      onChangeEconomicGroup,
    };
  },
  { persist: true },
);
